import { html } from "lit-html";
import { useRef } from "haunted";
import classNames from "classnames";
import { stationSelectorChevron, stationSelectorDivider } from "./newFlightSvgs";
import { useStationSelectorDropdown } from "./useStationSelectorDropdown";
import { useEffectWithCleanup, useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import { ref } from "Components/directives/ref";
import { City, RoutePickerModel } from "../RoutePickerModel";
import { useReduxState } from "Shared/redux/useReduxState";
import { stationUtils } from "./stationUtils";
import { useStations } from "ComponentHelpers/useStations";
import { INBOUND, OUTBOUND } from "Services/constants";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { DEFAULT_CURRENT_SEARCH } from "../../../../../shared/managers/current-search-manager/CurrentSearch";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { useMobileSearchboxStationSelectorModal } from "Components/searchbox/searchTab/flights/station-selector/useMobileSearchboxStationSelectorModal";
import { isTabletOrMobileResolution } from "Services/common";
import { CurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";

export interface Props {
	currentSearchManager: CurrentSearchManager;
	culture: string;
	dynamicSettings: DynamicSettings;
	startLoading: () => LoaderInstance;
	stopLoading: (loader: LoaderInstance) => void;
}

export const useStationSelector = (props: Props) => {
	const [countries] = useReduxState("countries");

	const { getRouteModel } = stationUtils();

	const originSelector = useRef<HTMLDivElement>(null);
	const destinationSelector = useRef<HTMLDivElement>(null);

	const [openSelector, setOpenSelector] = useState<"origin" | "destination" | null>(null);
	const [originModel, setOriginModel] = useState<RoutePickerModel>(undefined);
	const [destinationModel, setDestinationModel] = useState<RoutePickerModel>(undefined);

	useStations({
		opts: { culture: props.culture },
		startLoading: props.startLoading,
		stopLoading: props.stopLoading,
	});

	const init = () => {
		const scrollHandler = () => setOpenSelector(null);
		document.addEventListener("scroll", scrollHandler);

		if (!countries?.length) {
			return () => document.removeEventListener("scroll", scrollHandler);
		}

		const newOriginModel = getRouteModel(countries);
		setOriginModel(newOriginModel);

		return () => document.removeEventListener("scroll", scrollHandler);
	};

	const subscribeToClickOutside = () => {
		const onClickOutside = (e: any) => {
			const path = e.path || (e.composedPath && e.composedPath());
			const isClickInside =
				openSelector === "origin"
					? path.indexOf(originSelector.current) > -1
					: path.indexOf(destinationSelector.current) > -1;

			if (!isClickInside) setOpenSelector(null);
		};

		document.addEventListener("click", onClickOutside);

		return () => document.removeEventListener("click", onClickOutside);
	};

	const mobileOriginModal = useMobileSearchboxStationSelectorModal({
		journeyType: "origin",
		currentSearchManager: props.currentSearchManager,
		journeyIndex: OUTBOUND,
		journeyCountries: originModel?.countries,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		onInput: () => props.currentSearchManager.setOriginCity(undefined),
		onClick: (city: City, e: MouseEvent) => {
			props.currentSearchManager.setDestinationCity(undefined);
			props.currentSearchManager.setDepartureDate(undefined);
			props.currentSearchManager.setReturnDate(undefined);
			props.currentSearchManager.setOriginCity(city);
			props.currentSearchManager.setPassengers(DEFAULT_CURRENT_SEARCH.passengers);
			setOpenSelector(null);
			mobileOriginModal.close(e);

			window.setTimeout(() => {
				mobileDestinationModal.open();
			}, 350);
		},
	});

	const originDropdown = useStationSelectorDropdown({
		countries: originModel?.countries,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isOpen: openSelector === "origin",
		journeyIndex: OUTBOUND,
		originCity: props.currentSearchManager.currentSearch.originCity,
		destinationCity: props.currentSearchManager.currentSearch.destinationCity,
		onInput: () => {
			if (openSelector !== "origin") setOpenSelector("origin");
			props.currentSearchManager.setOriginCity(undefined);
		},
		onClick: (city: City) => {
			props.currentSearchManager.setOriginCity(city);
			props.currentSearchManager.setDestinationCity(undefined);
			props.currentSearchManager.setDepartureDate(undefined);
			props.currentSearchManager.setReturnDate(undefined);
			props.currentSearchManager.setPassengers(DEFAULT_CURRENT_SEARCH.passengers);

			applyOriginSelection();
		},
	});

	const mobileDestinationModal = useMobileSearchboxStationSelectorModal({
		journeyType: "destination",
		currentSearchManager: props.currentSearchManager,
		culture: props.culture,
		journeyIndex: INBOUND,
		journeyCountries: destinationModel?.countries,
		dynamicSettings: props.dynamicSettings,
		onInput: () => props.currentSearchManager.setDestinationCity(undefined),
		onClick: (city: City, e: MouseEvent) => {
			props.currentSearchManager.setDestinationCity(city);
			props.currentSearchManager.setDepartureDate(undefined);
			props.currentSearchManager.setReturnDate(undefined);
			props.currentSearchManager.setPassengers(DEFAULT_CURRENT_SEARCH.passengers);

			setOpenSelector(null);
			mobileDestinationModal.close(e);
		},
	});

	const destinationDropdown = useStationSelectorDropdown({
		countries: destinationModel?.countries,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isOpen: openSelector === "destination",
		journeyIndex: INBOUND,
		originCity: props.currentSearchManager.currentSearch.originCity,
		destinationCity: props.currentSearchManager.currentSearch.destinationCity,
		onInput: () => {
			if (openSelector !== "destination") setOpenSelector("destination");
			props.currentSearchManager.setDestinationCity(undefined);
		},
		onClick: (city: City) => {
			props.currentSearchManager.setDestinationCity(city);
			props.currentSearchManager.setDepartureDate(undefined);
			props.currentSearchManager.setReturnDate(undefined);
			props.currentSearchManager.setPassengers(DEFAULT_CURRENT_SEARCH.passengers);

			applyDestinationSelection();
		},
	});

	const applyOriginSelection = () => {
		setOpenSelector(null);

		// DEVNOTE Only open selector after model is set - cannot be useEffect, because side effects
		window.setTimeout(() => setOpenSelector("destination"), 100);
	};

	const applyDestinationSelection = () => {
		setOpenSelector(null);
	};

	const handleSwapStations = () => {
		if (
			!props.currentSearchManager.currentSearch.originCity ||
			!props.currentSearchManager.currentSearch.destinationCity
		) {
			return;
		}
		props.currentSearchManager.swapStations();
	};

	useEffectWithoutCleanup(() => {
		if (props.currentSearchManager.currentSearch.originCity && countries?.length) {
			const newDestinationModel = getRouteModel(countries, props.currentSearchManager.currentSearch.originCity);
			setDestinationModel(newDestinationModel);
		}
	}, [props.currentSearchManager.currentSearch.originCity?.code, countries]);

	useEffectWithCleanup(subscribeToClickOutside, [openSelector]);

	useEffectWithCleanup(init, [countries?.length]);

	const handleSelectorClick = (type: "origin" | "destination") => {
		PUB_SUBS.StationSelectorOpened.publish({});

		// DEVNOTE: Mobile modal behavior:
		if (isTabletOrMobileResolution()) {
			PUB_SUBS.MobileSearchboxModalOpened.publish({});

			if (type === "origin") {
				mobileOriginModal.open();
				return;
			} else {
				mobileDestinationModal.open();
				return;
			}
		}

		// DEVNOTE: Desktop dropdownbehavior:
		let timer: number;

		const scrollHandler = () => {
			window.clearTimeout(timer);

			timer = window.setTimeout(() => {
				document.removeEventListener("scroll", scrollHandler);
				setOpenSelector(type);
			}, 100);
		};

		document.addEventListener("scroll", scrollHandler);

		scrollHandler();
	};

	const originTemplate = () => html`
		<div
			ref=${ref(originSelector)}
			class=${classNames(
				"relative flex h-full w-full items-center rounded-t-lg border border-solid bg-white pl-4",
				"md:w-1/2 md:rounded-l-lg md:rounded-r-none md:pr-6",
				{
					"border-[#c1c1c1]":
						openSelector !== "origin" && !props.currentSearchManager.currentSearch.originCity,
					"border-n-cyan": openSelector === "origin" && !props.currentSearchManager.currentSearch.originCity,
					"border-n-blue": props.currentSearchManager.currentSearch.originCity,
				}
			)}
			@click=${() => handleSelectorClick("origin")}
		>
			<div class=${classNames("absolute right-6 top-1/2 -translate-y-1/2 transform hidden", "md:block")}>
				${stationSelectorChevron}
			</div>
			${mobileOriginModal.htmlTemplate()} ${originDropdown.htmlTemplate()}
		</div>
	`;

	const destinationTemplate = () => html`
		<div
			ref=${ref(destinationSelector)}
			class=${classNames(
				"relative flex h-full w-full cursor-pointer items-center rounded-b-lg border border-t-0 border-solid bg-white pl-4",
				"md:w-1/2 md:rounded-l-none md:rounded-r-lg md:border-t md:pl-6 md:pr-4",
				{
					"border-[#c1c1c1]":
						openSelector !== "destination" && !props.currentSearchManager.currentSearch.destinationCity,
					"border-n-cyan":
						openSelector === "destination" && !props.currentSearchManager.currentSearch.destinationCity,
					"border-n-blue": props.currentSearchManager.currentSearch.destinationCity,
					"pointer-events-none opacity-75": !props.currentSearchManager.currentSearch.originCity,
				}
			)}
			@click=${() => handleSelectorClick("destination")}
		>
			<div class=${classNames("absolute right-4 top-1/2 -translate-y-1/2 transform hidden", "md:block")}>
				${stationSelectorChevron}
			</div>
			${mobileDestinationModal.htmlTemplate()} ${destinationDropdown.htmlTemplate()}
		</div>
	`;

	const dividerTemplate = () => html`
		<div
			class=${classNames(
				"absolute right-4 top-1/2 z-10 h-8 w-8 -translate-y-1/2 rotate-90 transform text-white",
				"md:left-1/2 md:right-auto md:-translate-x-1/2 md:rotate-0 cursor-pointer"
			)}
			@click=${handleSwapStations}
		>
			${stationSelectorDivider}
		</div>
	`;

	const htmlTemplate = () => html`
		<div
			class=${classNames(
				"relative col-span-2 !mx-0 flex h-[68px] w-full flex-col items-center !px-0 font-body text-base text-[#c1c1c1]",
				"md:col-span-1 md:h-[42px] md:w-auto md:flex-row"
			)}
		>
			${originTemplate()} ${dividerTemplate()} ${destinationTemplate()}
		</div>
	`;

	return { htmlTemplate };
};
